/**
 * Shared, pure formatting/tally helpers for data summaries.
 *
 * Lifted verbatim from `localBackend.ts` (where they power
 * `answerDataQuestion`) so the agent-context builder (`context.ts`) can
 * reuse the exact same honest math instead of duplicating it. Behaviour is
 * unchanged — localBackend imports these and its tests stay green.
 */

/** Compact USD: $0 / $850 / $48k / $1.2M. */
export function usd(n: number | undefined): string {
  if (!n || n <= 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
}

/** "1 prospect" / "3 prospects". */
export function plural(n: number, one: string, many?: string): string {
  return `${n} ${n === 1 ? one : many ?? `${one}s`}`;
}

/** Tally a list by a key into "a: 2 · b: 1" form, skipping empty buckets. */
export function tally<T>(items: T[], key: (t: T) => string | undefined): string {
  const counts = new Map<string, number>();
  for (const it of items) {
    const k = key(it);
    if (!k) continue;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return [...counts.entries()].map(([k, v]) => `${k}: ${v}`).join(' · ');
}

/** Cap a list to `max` lines, appending "+N more" when truncated. */
export function capped(lines: string[], max = 8): string {
  if (lines.length <= max) return lines.join('\n');
  return `${lines.slice(0, max).join('\n')}\n…and ${lines.length - max} more.`;
}
